import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/hooks/useCart";

interface ProductCardProps {
  id: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  price: number;
  compareAtPrice: number | null;
  imageUrl: string;
  rating: number;
  reviewCount: number;
  isNew: boolean;
  isSale: boolean;
  inStock?: boolean;
  stockCount?: number;
  onQuickView?: () => void;
}

export default function ProductCard({
  id,
  nameEn,
  nameAr,
  slug,
  price,
  compareAtPrice,
  imageUrl,
  rating,
  reviewCount,
  isNew,
  isSale,
  inStock = true,
  stockCount = 99,
  onQuickView,
}: ProductCardProps) {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const name = isAr ? nameAr : nameEn;
  const { addItem } = useCart();
  const [imgError, setImgError] = useState(false);

  const discount = compareAtPrice
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inStock) return;
    addItem({
      productId: id,
      nameEn,
      nameAr,
      slug,
      price,
      imageUrl,
      quantity: 1,
      maxStock: stockCount,
      selectedVariants: {},
    });
  };

  return (
    <Link
      to={`/product/${slug}`}
      className="group block cursor-pointer"
    >
      {/* Image container */}
      <div className="relative aspect-square rounded-xl overflow-hidden bg-background-100 mb-4">
        {imgError || !imageUrl ? (
          <div className="w-full h-full flex items-center justify-center bg-background-200">
            <div className="flex flex-col items-center gap-2">
              <i className="ri-image-line text-3xl text-foreground-400"></i>
              <span className="text-xs text-foreground-400 px-3 text-center line-clamp-2">{name}</span>
            </div>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {isSale && compareAtPrice && (
            <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-semibold rounded-md whitespace-nowrap">
              -{discount}%
            </span>
          )}
          {isNew && (
            <span className="px-2.5 py-1 bg-foreground-950 text-white text-xs font-semibold rounded-md whitespace-nowrap">
              NEW
            </span>
          )}
        </div>

        {/* Quick add-to-cart button */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2">
          {onQuickView && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickView();
              }}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white text-foreground-700 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
              aria-label="Quick view"
            >
              <i className="ri-eye-line"></i>
            </button>
          )}
          <button
            onClick={handleAddToCart}
            disabled={!inStock}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
              inStock
                ? "bg-white text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
                : "bg-foreground-200 text-foreground-400 cursor-not-allowed"
            }`}
            aria-label="Add to cart"
          >
            <i className="ri-shopping-bag-line"></i>
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="px-0.5">
        <h3 className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2 leading-snug">
          {name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <i
                key={i}
                className={`text-xs ${
                  i < Math.floor(rating)
                    ? "ri-star-fill text-accent-500"
                    : i < rating
                    ? "ri-star-half-line text-accent-500"
                    : "ri-star-line text-foreground-300"
                }`}
              ></i>
            ))}
          </div>
          <span className="text-xs text-foreground-500">({reviewCount})</span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-foreground-950">
            ${price.toFixed(2)}
          </span>
          {compareAtPrice && (
            <span className="text-sm text-foreground-400 line-through">
              ${compareAtPrice.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}